import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api',
  timeout: 30000, // 30 seconds for image processing
  withCredentials: true, // Send cookies with requests for session tracking
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    const logData = {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      timeout: config.timeout,
      headers: { ...config.headers },
    };
    
    // Log file size if it's a FormData request
    if (config.data instanceof FormData) {
      const imageFile = config.data.get('image');
      if (imageFile) {
        logData.fileSize = imageFile.size;
        logData.fileName = imageFile.name || 'unknown';
        logData.fileType = imageFile.type || 'unknown';
      }
    }
    
    console.log('📤 API Request:', logData);
    return config;
  },
  (error) => {
    console.error('❌ Request Interceptor Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.config?.url,
      method: response.config?.method?.toUpperCase(),
    });
    return response;
  },
  (error) => {
    // Log detailed error information
    const errorDetails = {
      message: error.message,
      code: error.code,
      name: error.name,
      config: {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        baseURL: error.config?.baseURL,
        timeout: error.config?.timeout,
        fullURL: error.config ? `${error.config.baseURL}${error.config.url}` : 'unknown',
      },
    };

    if (error.response) {
      // Server responded with error status
      errorDetails.response = {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
      };
      console.error('❌ API Error (Response):', errorDetails);
    } else if (error.request) {
      // Request made but no response received
      errorDetails.request = {
        readyState: error.request.readyState,
        status: error.request.status,
        statusText: error.request.statusText,
        responseURL: error.request.responseURL,
        responseText: error.request.responseText?.substring(0, 200), // First 200 chars
      };
      console.error('❌ API Error (No Response):', errorDetails);
      console.error('❌ Request Object:', {
        readyState: error.request.readyState,
        status: error.request.status,
        statusText: error.request.statusText,
        responseURL: error.request.responseURL,
        timeout: error.config?.timeout,
        timestamp: new Date().toISOString(),
      });
    } else {
      // Something else happened
      console.error('❌ API Error (Other):', errorDetails);
    }

    // Format error message for user-friendly display
    const userFriendlyError = formatError(error);
    return Promise.reject(userFriendlyError);
  }
);

/**
 * Format error for user-friendly display
 */
const formatError = (error) => {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const message = error.response.data?.message || error.response.data?.error;

    switch (status) {
      case 400:
        return new Error(message || 'Invalid request. Please check your input.');
      case 404:
        return new Error('Service not found. Please ensure the backend is running.');
      case 413:
        return new Error('Image file is too large. Please upload a smaller image.');
      case 500:
        return new Error(message || 'Server error. Please try again later.');
      default:
        return new Error(message || `Request failed with status ${status}`);
    }
  } else if (error.request) {
    // Request made but no response received
    const errorCode = error.code;
    const timeout = error.config?.timeout;
    const url = error.config ? `${error.config.baseURL}${error.config.url}` : 'unknown';
    
    console.error('🔍 Connection Error Details:', {
      code: errorCode,
      message: error.message,
      timeout: timeout,
      url: url,
      readyState: error.request?.readyState,
      timestamp: new Date().toISOString(),
    });

    // Provide more specific error messages based on error code
    let errorMessage = 'Unable to connect to server. Please check if the backend is running.';
    if (errorCode === 'ECONNABORTED' || errorCode === 'ETIMEDOUT') {
      errorMessage = `Request timed out after ${timeout}ms. The server may be slow or unresponsive.`;
    } else if (errorCode === 'ERR_NETWORK' || errorCode === 'ERR_INTERNET_DISCONNECTED') {
      errorMessage = 'Network error. Please check your internet connection.';
    } else if (errorCode === 'ERR_CONNECTION_REFUSED') {
      errorMessage = 'Connection refused. Please ensure the backend server is running.';
    } else if (errorCode) {
      errorMessage = `Connection error (${errorCode}). Please check if the backend is running.`;
    }
    
    return new Error(errorMessage);
  } else {
    // Something else happened
    console.error('🔍 Unexpected Error:', {
      message: error.message,
      stack: error.stack,
      name: error.name,
    });
    return new Error(error.message || 'An unexpected error occurred.');
  }
};

/**
 * Search for similar jewelry by image URL
 * @param {string} imageUrl - URL of the image to search
 * @returns {Promise<Object>} Search results
 */
export const searchByUrl = async (imageUrl) => {
  try {
    const response = await api.post('/search/url', { imageUrl });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Enhance an uploaded image
 * @param {File} imageFile - Image file to enhance
 * @returns {Promise<Object>} Enhanced image data
 */
export const enhanceImage = async (imageFile) => {
  try {
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('image', imageFile);

    const response = await api.post('/search/enhance', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Remove background from an uploaded image
 * @param {File|Blob} imageFile - Image file or blob to process
 * @param {string} filename - Optional filename for Blob uploads
 * @returns {Promise<Object>} Image with background removed
 */
export const removeBackground = async (imageFile, filename = 'image.jpg') => {
  try {
    // Create FormData for file upload
    const formData = new FormData();

    // Handle both File and Blob
    if (imageFile instanceof File) {
      formData.append('image', imageFile);
    } else {
      // For Blob (enhanced image), add filename
      formData.append('image', imageFile, filename);
    }

    const response = await api.post('/search/remove-background', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Search for similar jewelry by uploading an image file
 * @param {File|Blob} imageFile - Image file or blob to upload
 * @param {string} filename - Optional filename for Blob uploads
 * @param {Object} imageUrls - Optional object containing originalImageUrl, enhancedImageUrl, backgroundRemovedImageUrl
 * @returns {Promise<Object>} Search results
 */
export const searchByUpload = async (imageFile, filename = 'image.jpg', imageUrls = {}) => {
  const startTime = Date.now();
  try {
    console.log('🔍 searchByUpload called:', {
      filename,
      fileType: imageFile instanceof File ? 'File' : 'Blob',
      fileSize: imageFile.size,
      imageUrls: {
        hasOriginal: !!imageUrls.originalImageUrl,
        hasEnhanced: !!imageUrls.enhancedImageUrl,
        hasBackgroundRemoved: !!imageUrls.backgroundRemovedImageUrl,
        searchedImageType: imageUrls.searchedImageType,
      },
      timestamp: new Date().toISOString(),
    });

    // Create FormData for file upload
    const formData = new FormData();

    // Handle both File and Blob
    if (imageFile instanceof File) {
      formData.append('image', imageFile);
      console.log('📎 Added File to FormData:', {
        name: imageFile.name,
        size: imageFile.size,
        type: imageFile.type,
      });
    } else {
      // For Blob (enhanced image), add filename
      formData.append('image', imageFile, filename);
      console.log('📎 Added Blob to FormData:', {
        filename,
        size: imageFile.size,
        type: imageFile.type,
      });
    }

    // Add image URLs if provided (for saving to history)
    if (imageUrls.originalImageUrl) {
      formData.append('originalImageUrl', imageUrls.originalImageUrl);
    }
    if (imageUrls.enhancedImageUrl) {
      formData.append('enhancedImageUrl', imageUrls.enhancedImageUrl);
    }
    if (imageUrls.backgroundRemovedImageUrl) {
      formData.append('backgroundRemovedImageUrl', imageUrls.backgroundRemovedImageUrl);
    }
    if (imageUrls.searchedImageType) {
      formData.append('searchedImageType', imageUrls.searchedImageType);
      console.log('📤 API Service: Added searchedImageType to FormData:', imageUrls.searchedImageType);
    } else {
      console.warn('⚠️ API Service: searchedImageType is missing from imageUrls!');
    }

    console.log('🚀 Sending search request...');
    const response = await api.post('/search/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const duration = Date.now() - startTime;
    console.log('✅ Search completed successfully:', {
      duration: `${duration}ms`,
      resultsCount: response.data?.results?.length || 0,
      timestamp: new Date().toISOString(),
    });

    return response.data;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error('❌ Search failed:', {
      duration: `${duration}ms`,
      error: error.message,
      filename,
      fileSize: imageFile?.size,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Check API health status
 * @returns {Promise<Object>} Health check response
 */
export const healthCheck = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get search history with pagination
 * @param {number} page - Page number (0-indexed)
 * @param {number} limit - Number of items per page (default: 50)
 * @returns {Promise<Object>} History data with pagination info
 */
export const getHistory = async (page = 0, limit = 50) => {
  try {
    const skip = page * limit;
    const response = await api.get('/history', {
      params: { skip, limit }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get a specific search history entry by ID
 * @param {string} id - Search history ID
 * @returns {Promise<Object>} Search history details
 */
export const getHistoryById = async (id) => {
  try {
    const response = await api.get(`/history/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get product information from NetSuite by SKU
 * @param {string} sku - Product SKU (image name without extension)
 * @returns {Promise<Object>} NetSuite product data
 */
export const getNetSuiteProduct = async (sku) => {
  try {
    const response = await api.get(`/netsuite/product/${sku}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Named exports
export default api;
